import { ClientEvent, MatrixEvent, MatrixClient } from 'matrix-js-sdk/src/matrix';
import { useEffect } from 'react';

import type { CustomImage } from 'contexts/CustomImagesContext/CustomImagesProvider';
import { CustomMeedioMatrixEvents } from 'utils/matrixUtils';

import { loadCustomImagesFromMatrix, revokeBlobUrl } from './utils';

/**
 * Hook to load and sync custom images from Matrix account data
 */
export const useCustomImagesSync = (
  matrixClient: MatrixClient | null,
  blobUrlsRef: React.MutableRefObject<Map<string, string>>,
  cleanupUnusedBlobUrlsFn: (currentMxcUris: Set<string>) => void,
  setCustomImages: React.Dispatch<React.SetStateAction<CustomImage[]>>
) => {
  useEffect(() => {
    if (!matrixClient) {
      // Clean up all blob URLs when matrix client is unavailable
      blobUrlsRef.current.forEach(revokeBlobUrl);
      blobUrlsRef.current.clear();
      setCustomImages([]);
      return;
    }

    const loadImages = async () => {
      const images = await loadCustomImagesFromMatrix(matrixClient, blobUrlsRef, cleanupUnusedBlobUrlsFn);
      setCustomImages(images);
    };

    loadImages();

    // Listen for account data changes
    const handleAccountData = (event: MatrixEvent) => {
      if (event.getType() === CustomMeedioMatrixEvents.CUSTOM_BACKGROUND_IMAGES) loadImages();
    };

    matrixClient.on(ClientEvent.AccountData, handleAccountData);

    return () => {
      matrixClient.off(ClientEvent.AccountData, handleAccountData);
    };
  }, [matrixClient, cleanupUnusedBlobUrlsFn, setCustomImages, blobUrlsRef]);
};
